import React, { useEffect, useState } from "react";
import CardEmpty from "Utils/Cards/CardEmpty";
import Paginacion from "Utils/Components/Paginacion/Paginacion";
import { DecisionSwal } from "Utils/Utils/DecisionSwal";
import { NotificationManager } from "react-notifications";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import moment from "moment";
import { CierresMensualesInventarioApi } from "./api/CierresMensualesInventarioApi";
import { FiltrosCierresMensuales } from "./components/FiltrosCierresMensuales";
import { CierresMensualesTable } from "./components/CierresMensualesTable";
import LoadMask from "../../../../../../Utils/LoadMask/LoadMask";
import {
    ESTADOS_PERIODO_CIERRE_MENSUAL,
    cierreParaReporte,
    esTipoBodega,
} from "../../../../helpers/tiposCierreMensual";
import { getErrorFromResponse } from "../../../../../../../../utility/utils";

const STORAGE_KEY = "cierres_mensuales_inventario";

// Historial de cierres mensuales. Es la única vía para consultar un período ya
// cerrado, y va por período y no por cierre: el detalle y el Excel son del
// período completo, así que sus dos mitades se muestran en la misma fila aunque
// se hayan registrado en días distintos
export const CierresMensualesInventarioTab = () => {
    const savedState = JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
    const filtros = savedState && savedState.filtros ? savedState.filtros : {};

    const { control, watch } = useForm({
        defaultValues: {
            estado: filtros.estado
                ? ESTADOS_PERIODO_CIERRE_MENSUAL.find(
                      (opcion) => opcion.value === filtros.estado.value,
                  )
                : null,
            fecha_inicial: filtros.fecha_inicial
                ? moment(filtros.fecha_inicial)
                : null,
            fecha_final: filtros.fecha_final
                ? moment(filtros.fecha_final)
                : null,
        },
    });

    const estado = watch("estado");
    const fecha_inicial = watch("fecha_inicial");
    const fecha_final = watch("fecha_final");

    const [periodos, setPeriodos] = useState([]);
    const [cantidadResultados, setCantidadResultados] = useState(0);
    const [paginaSeleccionada, setPaginaSeleccionada] = useState(
        savedState.pagina || 1,
    );

    const {
        getCierresMensuales,
        anularCierreMensual,
        obtenerReporteExcel,
        cargando,
    } = CierresMensualesInventarioApi();

    const GetPeriodos = async () => {
        try {
            const params = {
                page: paginaSeleccionada,
            };

            if (estado) {
                params.estado = estado.value;
            }

            // el rango se compara contra el fin del período
            if (fecha_inicial) {
                params.fecha_inicial =
                    moment(fecha_inicial).format("YYYY-MM-DD");
            }

            if (fecha_final) {
                params.fecha_final = moment(fecha_final).format("YYYY-MM-DD");
            }

            const response = await getCierresMensuales(params);

            setPeriodos(response.results || []);
            setCantidadResultados(response.count || 0);
        } catch (error) {
            // anular el último puede dejar vacía la página en la que se estaba
            if (
                error &&
                error.detail === "Invalid page." &&
                paginaSeleccionada > 1
            ) {
                setPaginaSeleccionada((prev) => prev - 1);
                return;
            }

            const errorMsg = getErrorFromResponse(
                error,
                "Error al obtener los cierres mensuales",
            );
            NotificationManager.error(errorMsg, "Error", 3000);

            setPeriodos([]);
            setCantidadResultados(0);
        }
    };

    useEffect(() => {
        GetPeriodos();
    }, [paginaSeleccionada, estado, fecha_inicial, fecha_final]);

    useEffect(() => {
        sessionStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({
                filtros: { estado, fecha_inicial, fecha_final },
                pagina: paginaSeleccionada,
            }),
        );
    }, [estado, fecha_inicial, fecha_final, paginaSeleccionada]);

    // el Excel es del período completo, así que se pide por cualquiera de sus
    // dos cierres
    const ExportarPeriodo = async (periodo) => {
        try {
            await obtenerReporteExcel(cierreParaReporte(periodo).id);
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al obtener el reporte en Excel",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    const AnularCierreMensual = async (cierre) => {
        try {
            await anularCierreMensual(cierre.id);

            NotificationManager.success(
                "Cierre mensual anulado",
                "Éxito",
                3000,
            );

            // cambia cuál es el último y por lo tanto quién se puede anular
            GetPeriodos();
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al anular el cierre mensual",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    // solo el de bodega tiene fecha de corte que nombrar y un documento que
    // vuelve a poder anularse: el cierre de inventario de bodega que usó
    const ConfirmarAnulacion = (cierre) => {
        const esBodega = esTipoBodega(cierre.tipo);

        const corte = esBodega
            ? ", con corte al " +
              moment(cierre.fecha_corte).format("DD/MM/YYYY HH:mm")
            : "";

        const documento = esBodega
            ? " El cierre de inventario de bodega utilizado podrá anularse nuevamente."
            : "";

        const mensaje =
            "Se anulará el cierre mensual de " +
            cierre.tipo_display.toLowerCase() +
            " del período " +
            cierre.periodo +
            corte +
            "." +
            documento;

        DecisionSwal(mensaje, "Sí, anular", "Cancelar", () =>
            AnularCierreMensual(cierre),
        );
    };

    return (
        <div className="reporte-inventarios__tab-wrapper">
            <CardEmpty noShadow>
                <LoadMask loading={cargando} blur>
                    <div className="reporte-inventarios__table-header">
                        <FiltrosCierresMensuales
                            control={control}
                            onRefresh={GetPeriodos}
                            resetPage={() => setPaginaSeleccionada(1)}
                        />

                        <div className="ml-auto formulario-fields-row flex-nowrap">
                            <Link to="/inventarios-almacen/reporte/crear-cierre-mensual">
                                <button className="btn btn-primary text-nowrap">
                                    Generar reporte de cierre mensual
                                </button>
                            </Link>
                        </div>
                    </div>

                    <CierresMensualesTable
                        periodos={periodos}
                        onExportar={ExportarPeriodo}
                        onAnular={ConfirmarAnulacion}
                    />
                    <Paginacion
                        cantidadResultados={cantidadResultados}
                        paginaSeleccionada={paginaSeleccionada}
                        setPaginaSeleccionada={setPaginaSeleccionada}
                    />
                </LoadMask>
            </CardEmpty>
        </div>
    );
};
